import { Timestamp } from '@angular/fire/firestore';
import { IGlobalError } from '../../../shared/shared-components/components/global-error-display/models/global-error';
import { ILastStockTakeSyncStatus } from './stock-take-date-sync.interface';
import { IStockTakeDashboardData } from './stock-take-dashboard.interface';
import { IStockItemApiData, IStockItemMovement, IStockTakeFinalFigure } from './stock-take-item.interface';
import { IStockTake, StockTakeDocumentStatus } from './stock-take.interface';
import { IStockTakeItem } from './stock-take-item.interface';
import { IStockTakeScanSession } from './stock-take-scan-session.interface';
import { ISharedLoadingProgressPresentation } from '../../../shared/shared-models/loading/shared-loading-progress.interface';

/**
 * NgRx state for stock takes feature
 */
export interface IStockTakeState {
  activeScanSession: IStockTakeScanSession | null;
  adjustmentItems: Record<string, IStockTakeItem[]>;
  adjustments: Record<string, IStockTake[]>;
  /** Item code for unknown-barcode assign flow — qty modal opens after items reload */
  assignBarcodeFlowCode: string | null;
  /** Scanned barcode that could not be resolved to any stock item in the collection */
  barcodeNotFoundBarcode: string | null;
  /** Whether another page of completed stock takes can still be fetched */
  completedHasMore: Record<string, boolean>;
  /** Outcome of the latest create-stock-take-from-process action; cleared on the next dispatch. */
  createStockTakeFromProcessResult: 'success' | 'failure' | null;
  currentAdjustment: IStockTake | null;
  dashboardData: IStockTakeDashboardData | null;
  dashboardLastRefreshed: Date | null;
  /** Timestamp of the last delcolissuestran sync from the server (from data_timestamps) */
  delColIssuesTransTimestamp: Date | null;
  errors: IGlobalError[];
  /** Finalised item snapshots keyed by adjustmentId — loaded for terminal report-mode stock takes */
  finalisedItems: Record<string, IStockTakeFinalFigure[]>;
  isAdjustmentDetailsLoading: boolean;
  isAdjustmentsLoading: boolean;
  /** True while linking an unknown barcode to an item before the count modal opens */
  isAssigningBarcode: boolean;
  isCompletedLoadingMore: boolean;
  /** True while bulk zero-count runs for all pending lines on an open stock take. */
  isCountingAllPendingAsZero: boolean;
  /** True while creating a follow-up stock take from the process warning modal. */
  isCreatingStockTakeFromProcess: boolean;
  isDashboardLoading: boolean;
  isDeletingStockTake: boolean;
  isFinalisedItemsLoading: boolean;
  isItemDetailsLoading: boolean;
  /** True until the first STOCK_INIT status emission arrives for the current store. */
  isLastStockTakeSyncStatusLoading: boolean;
  isMovementLoading: boolean;
  /** True while filter- or uncounted-based item population runs after create. */
  isPopulatingStockTakeItems: boolean;
  isProcessingAdjustment: boolean;
  /** True while removing stock-take lines that are missing from the shared stock file */
  isRemovingMissingStockTakeItems: boolean;
  isRequestingSync: boolean;
  isRetryingFailedStockTakeProcessing: boolean;
  /** True while a prepared stock take is being moved back to in progress. */
  isRevertingStockTakeToInProgress: boolean;
  isRunningSchedules: boolean;
  isStockTakeItemsLoading: boolean;
  /** True while counting stock items missing `_stock_take_date` in Typesense. */
  isTypesenseStockTakeDateCheckLoading: boolean;
  /** True when Firestore stock file enrichment failed for a stock take (e.g. permission denied). */
  itemDetailsLoadFailedByAdjustmentId: Record<string, boolean>;
  lastItemDetailsFetchTime: Date | null;
  /** Set once the first movement fetch completes (success or failure) — null until then */
  lastMovementFetchTime: Date | null;
  /** Error message from a failed STOCK_INIT observation doc or API trigger failure */
  lastStockTakeSyncError: string | null;
  /** Last successful sync completion time (`completedAt` on STOCK_INIT observation). */
  lastStockTakeSyncExecutedAt: Timestamp | null;
  /** User-facing message from the last successful stock init callable response (e.g. job started). */
  lastStockTakeSyncInfoMessage: string | null;
  lastStockTakeSyncItemsSynced: number | null;
  /** Firestore path from stock init callable — single-doc listener while a run is active. */
  lastStockTakeSyncObservationPath: string | null;
  /** Current run start time while PENDING (`executedAt` on observation — updates with progress). */
  lastStockTakeSyncProcessingStartedAt: Timestamp | null;
  lastStockTakeSyncStatus: ILastStockTakeSyncStatus | null;
  /** Store the current `lastStockTakeSyncStatus` belongs to; null until first load. */
  lastStockTakeSyncStatusStoreId: string | null;
  lastStockTakeSyncTotalItems: number | null;
  /** Route/detail load in flight — guards against stale detail responses when switching stock takes. */
  loadingAdjustmentId: string | null;
  missingItemCodes: string[];
  /** True when the last movement API call failed — data may be stale */
  movementDataOutOfSync: boolean;
  /** Original Firestore status for stock takes awaiting backend deletion cleanup. */
  pendingDeletionStatuses: Record<string, Record<string, StockTakeDocumentStatus>>;
  /** Barcode code pending quantity confirmation from the scan item modal */
  pendingScannedCode: string | null;
  /** Step-based progress while stock takes are created and items are written in batches. */
  populationProgress: ISharedLoadingProgressPresentation | null;
  /** Set when a retry completes; cleared when a new retry is dispatched. */
  retryFailedStockTakeProcessingOutcome: 'success' | 'failure' | null;
  /** Aggregated counts per status keyed by storeId */
  statusCounts: Record<string, Record<string, number>>;
  statusFilter: string | null;
  stockItemApiData: Record<string, Record<string, IStockItemApiData>>;
  /** Movement data per adjustment keyed by stock code */
  stockMovementData: Record<string, Record<string, IStockItemMovement>>;
  /** In-flight stock take line mutations (count, recount, delete, etc.). */
  stockTakeItemMutationCount: number;
  /** True while Firestore item snapshots are still arriving after a user mutation. */
  stockTakeItemsListenerSettling: boolean;
  /** Timestamp of the last stock data sync from the server (from data_timestamps) */
  stockTimestamp: Date | null;
  /** Timestamp of the last transactions sync from the server (from data_timestamps) */
  transactionsTimestamp: Date | null;
  /** Count of stock items missing `_stock_take_date` in Typesense; null until checked. */
  typesenseMissingStockTakeDateCount: number | null;
  /** Whether `_stock_take_date` exists in the store Typesense schema; null until checked. */
  typesenseStockTakeDateSchemaReady: boolean | null;
}
